import { useEffect, useRef } from "react";
import { SignInCard } from '@mayline-ai/platform-ui';
import {
  OneShotBridge,
  markSilentBootstrapAttempt,
  probePlatformSession,
  redirectToLogin,
} from "../lib/auth";
import { createSignInHandlers } from "../lib/signin-bridge";
import { isDevModeEnabled } from "../lib/dev-auth";

/**
 * The SINGLE unauthenticated surface for the admin dashboard.
 *
 * ProtectedRoute renders this when auth resolves to unauthenticated, so there
 * is exactly ONE signed-out experience: the shared @mayline-ai/platform-ui
 * SignInCard (Microsoft + Google) that ALSO fires a cooldown-gated silent probe
 * of the shared platform session. An already signed-in Parslee staffer is
 * bounced into the dashboard login flow exactly once; anyone else stays on the
 * sign-in card and signs in explicitly.
 *
 * The SignInCard is presentational-only: its two provider intents are wired to
 * the app-local `redirectToLogin(returnUrl, provider)` through the thin
 * `createSignInHandlers` bridge (no consent logic lives here).
 *
 * Two guards prevent a login loop:
 *   - The shared SDK `OneShotBridge` (one instance per mount) authorizes the
 *     handoff AT MOST once and only on a POSITIVE probe.
 *   - The redirect-spanning sessionStorage cooldown (`markSilentBootstrapAttempt`
 *     stamped HERE, before the full-document `redirectToLogin()` navigation).
 *
 * Skipped in dev mode (identity comes from headers, not the platform session).
 */
export function UnauthenticatedGate() {
  const probeInFlightRef = useRef(false);
  const bridgeRef = useRef<OneShotBridge | null>(null);

  useEffect(() => {
    if (isDevModeEnabled()) {
      return;
    }
    if (probeInFlightRef.current) {
      return;
    }
    probeInFlightRef.current = true;
    if (!bridgeRef.current) {
      bridgeRef.current = new OneShotBridge();
    }
    const bridge = bridgeRef.current;
    void probePlatformSession()
      .then((probe) => {
        if (bridge.evaluate(probe).shouldContinue) {
          markSilentBootstrapAttempt();
          redirectToLogin();
        }
      })
      .finally(() => {
        probeInFlightRef.current = false;
      });
  }, []);

  const handlers = createSignInHandlers();

  return (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', padding: 24 }}>
      <SignInCard onMicrosoft={handlers.onMicrosoft} onGoogle={handlers.onGoogle} />
    </div>
  );
}
